/**
 * SignaturenPage v4 — Operatives Signatur-Cockpit
 * Verwaltungszentrale für alle Signing-Requests (DocuSeal + Native Canvas)
 *
 * Fix (CLARA360-UI-MOBILE-FIX-1):
 *  - API-Key-Header (CLARA_API_TOKEN) zu allen Control-API-Aufrufen hinzugefügt
 *  - useAuth() für userId in property/list und tenant/list
 *  - Alle fetch-Aufrufe über controlFetch()-Helper geleitet
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, Clock, XCircle, AlertCircle,
  RefreshCw, Search, ChevronDown,
  Copy, Send, Trash2, User, Calendar, Loader2,
  Plus, ShieldCheck, Info, X, PenLine
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// ── Control-API Helper ────────────────────────────────────────────────────────
// CLARA_API_TOKEN — wird im Frontend über Vite-Env-Variable bereitgestellt
// Fallback: hardcoded Token (nur für interne Nutzung, kein öffentlicher Zugriff)
const CONTROL_API_KEY = import.meta.env.VITE_CONTROL_API_KEY || '4426538bb477fdc8fbadb837550d35b79c03f4a479e5cce55b50c9fc0cb56551';

async function controlFetch(path, options = {}) {
  const headers = {
    'x-api-key': CONTROL_API_KEY,
    ...(options.headers || {}),
  };
  const res = await fetch(path, { ...options, headers });
  return res;
}

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:    { label: 'Ausstehend',    color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', icon: Clock },
  sent:       { label: 'Versendet',     color: 'text-blue-400',   bg: 'bg-blue-400/10',   border: 'border-blue-400/30',   icon: Send },
  completed:  { label: 'Abgeschlossen', color: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/30',  icon: CheckCircle },
  signed:     { label: 'Signiert',      color: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/30',  icon: CheckCircle },
  declined:   { label: 'Abgelehnt',     color: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/30',    icon: XCircle },
  expired:    { label: 'Abgelaufen',    color: 'text-gray-400',   bg: 'bg-gray-400/10',   border: 'border-gray-400/30',   icon: AlertCircle },
  cancelled:  { label: 'Storniert',     color: 'text-gray-400',   bg: 'bg-gray-400/10',   border: 'border-gray-400/30',   icon: XCircle },
  error:      { label: 'Fehler',        color: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/30',    icon: AlertCircle },
  submitted:  { label: 'Eingereicht',   color: 'text-blue-400',   bg: 'bg-blue-400/10',   border: 'border-blue-400/30',   icon: Send },
  awaiting:   { label: 'Wartet',        color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', icon: Clock },
};

const ADAPTER_CONFIG = {
  docuseal:      { label: 'DocuSeal (E-Mail)', color: 'text-purple-400', bg: 'bg-purple-400/10' },
  native_canvas: { label: 'Vor-Ort (Tablet)',  color: 'text-blue-400',   bg: 'bg-blue-400/10'   },
};

const getStatus = (s) => STATUS_CONFIG[s] || { label: s || 'Unbekannt', color: 'text-gray-400', bg: 'bg-gray-400/10', border: 'border-gray-400/30', icon: Info };
const getAdapter = (a) => ADAPTER_CONFIG[a] || { label: a || '—', color: 'text-gray-400', bg: 'bg-gray-400/10' };

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

// ── Status-Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = getStatus(status);
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// ── Confirm-Dialog ────────────────────────────────────────────────────────────
const ConfirmDialog = ({ title, message, onConfirm, onCancel, danger }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
      <h3 className={`text-lg font-bold mb-2 ${danger ? 'text-red-400' : 'text-white'}`}>{title}</h3>
      <p className="text-gray-300 text-sm mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 text-sm font-medium transition-colors">
          Abbrechen
        </button>
        <button onClick={onConfirm} className={`px-4 py-2 rounded-xl text-white text-sm font-medium transition-colors ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
          Bestätigen
        </button>
      </div>
    </div>
  </div>
);

// ── Detail-Modal ──────────────────────────────────────────────────────────────
const DetailModal = ({ submission, auditEntries, onClose, onCancel, onRefresh, cancelling, refreshing }) => {
  const [copied, setCopied] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const handleCopy = async (text, key) => {
    const ok = await copyToClipboard(text);
    if (ok) { setCopied(key); setTimeout(() => setCopied(null), 2000); }
  };

  const adapterCfg = getAdapter(submission.adapter);
  const relevantAudit = auditEntries.filter(e =>
    (e.submission_id && String(e.submission_id) === String(submission.id)) ||
    (e.canvas_token && submission.canvas_token && e.canvas_token === submission.canvas_token) ||
    (e.signature_request_id && submission.signature_request_id && e.signature_request_id === submission.signature_request_id)
  );

  const canCancel = !['completed', 'signed', 'cancelled'].includes(submission.status) && submission.adapter !== 'native_canvas';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {confirmCancel && (
        <ConfirmDialog
          title="Submission stornieren?"
          message={`Submission #${submission.id} wird storniert. Diese Aktion kann nicht rückgängig gemacht werden.`}
          danger
          onConfirm={() => { setConfirmCancel(false); onCancel(submission.id); }}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <div>
            <h2 className="text-lg font-bold text-white">
              {submission.template_label || `Submission #${submission.id}`}
            </h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <StatusBadge status={submission.status} />
              <span className={`text-xs px-2 py-0.5 rounded-full ${adapterCfg.bg} ${adapterCfg.color}`}>
                {adapterCfg.label}
              </span>
              {submission.id && !String(submission.id).startsWith('canvas_') && (
                <span className="text-xs text-gray-500 font-mono">#{submission.id}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Metadaten */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-700/40 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Erstellt</p>
              <p className="text-sm text-white">{fmtDate(submission.created_at)}</p>
            </div>
            <div className="bg-gray-700/40 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Abgeschlossen</p>
              <p className="text-sm text-white">{fmtDate(submission.completed_at)}</p>
            </div>
            {submission.property_id && (
              <div className="bg-gray-700/40 rounded-xl p-3 col-span-2">
                <p className="text-xs text-gray-500 mb-1">Objekt-ID</p>
                <p className="text-sm text-white font-mono text-xs">{submission.property_id}</p>
              </div>
            )}
          </div>

          {/* Parteien */}
          {submission.submitters?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-blue-400" /> Parteien
              </h3>
              <div className="space-y-2">
                {submission.submitters.map((s, i) => (
                  <div key={i} className="bg-gray-700/40 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white truncate">{s.name || '—'}</span>
                        <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded-full">{s.role}</span>
                        <StatusBadge status={s.status} />
                      </div>
                      {s.email && <p className="text-xs text-gray-400 mt-0.5">{s.email}</p>}
                    </div>
                    {s.sign_url && (
                      <button
                        onClick={() => handleCopy(s.sign_url, `sign_${i}`)}
                        className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 bg-blue-400/10 px-2 py-1 rounded-lg transition-colors"
                        title="Signing-Link kopieren"
                      >
                        {copied === `sign_${i}` ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied === `sign_${i}` ? 'Kopiert' : 'Link'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Native Canvas Ergebnis */}
          {submission.adapter === 'native_canvas' && (submission.audit_id || submission.vault_asset_id) && (
            <div className="bg-green-400/5 border border-green-400/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Vor-Ort-Signatur Ergebnis
              </h3>
              <div className="space-y-1 text-sm">
                {submission.audit_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Audit-ID</span>
                    <span className="text-white font-mono">{submission.audit_id}</span>
                  </div>
                )}
                {submission.vault_asset_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Vault-Asset</span>
                    <span className="text-white font-mono text-xs">{submission.vault_asset_id}</span>
                  </div>
                )}
                {submission.signed_by_tenant && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Mieter</span>
                    <span className="text-white">{submission.signed_by_tenant}</span>
                  </div>
                )}
                {submission.signed_by_owner && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Vermieter</span>
                    <span className="text-white">{submission.signed_by_owner}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fehlertext */}
          {submission.error_message && (
            <div className="bg-red-400/5 border border-red-400/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-red-400 mb-1 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Fehler
              </h3>
              <p className="text-sm text-red-300 font-mono">{submission.error_message}</p>
            </div>
          )}

          {/* Audit-Trail */}
          {relevantAudit.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-400" /> Audit-Trail
              </h3>
              <div className="space-y-2">
                {relevantAudit.map((entry, i) => (
                  <div key={i} className="bg-gray-700/30 rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-blue-400 font-medium">{entry.event || entry.final_status}</span>
                      <span className="text-gray-500">{fmtDate(entry.logged_at || entry.requested_at)}</span>
                    </div>
                    {entry.final_status && (
                      <span className="text-gray-400">Status: <span className="text-white">{entry.final_status}</span></span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aktionen */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
            {submission.adapter !== 'native_canvas' && (
              <button
                onClick={() => onRefresh(submission.id)}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm font-medium transition-colors disabled:opacity-40"
              >
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Status aktualisieren
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => setConfirmCancel(true)}
                disabled={cancelling}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm font-medium transition-colors disabled:opacity-40"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Stornieren
              </button>
            )}
            {!canCancel && submission.adapter === 'native_canvas' && (
              <span className="text-xs text-gray-500 self-center">Vor-Ort-Signaturen können nicht storniert werden.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Neue Anfrage (Inline-Formular) ────────────────────────────────────────────
const NeueAnfrage = ({ onClose, onSuccess, userId }) => {
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [ownerData, setOwnerData] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Templates laden (API-Key über controlFetch)
    controlFetch('/api/control/signature/templates')
      .then(r => r.json())
      .then(d => setTemplates(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});

    // Properties laden — userId wird benötigt
    if (userId) {
      controlFetch('/api/control/property/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      })
        .then(r => r.json())
        .then(d => setProperties(Array.isArray(d.data) ? d.data : []))
        .catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    if (!selectedProperty) { setTenants([]); setOwnerData(null); return; }
    setLoadingTenants(true);

    // Tenants für gewähltes Objekt laden
    controlFetch('/api/control/tenant/list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, filters: { propertyId: selectedProperty } }),
    })
      .then(r => r.json())
      .then(d => setTenants(Array.isArray(d.data) ? d.data : []))
      .catch(() => setTenants([]))
      .finally(() => setLoadingTenants(false));

    // Owner-Daten laden
    controlFetch(`/api/control/signature/owner/${selectedProperty}`)
      .then(r => r.json())
      .then(d => setOwnerData(d))
      .catch(() => setOwnerData(null));
  }, [selectedProperty, userId]);

  const handleSubmit = async () => {
    if (!selectedTemplate || !selectedProperty || !selectedTenant) {
      setError('Bitte Template, Objekt und Mieter auswählen.'); return;
    }
    setLoading(true); setError(null);
    const tenant = tenants.find(t => String(t.id) === String(selectedTenant));
    const property = properties.find(p => String(p.id) === String(selectedProperty));
    const template = templates.find(t => String(t.id) === String(selectedTemplate));
    const ownerFields = ownerData?.owner || {};
    try {
      const res = await controlFetch('/api/control/signature/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: parseInt(selectedTemplate), template_label: template?.label || '',
          tenant_id: selectedTenant, tenant_name: tenant?.name || '', tenant_email: tenant?.email || '',
          property_id: selectedProperty, property_name: property?.property_name || property?.name || '',
          owner_id: ownerFields.owner_id || null, owner_fields: ownerFields,
          submitters: [
            { name: tenant?.name || 'Mieter', email: tenant?.email || '', role: 'Mieter', send_email: true },
            ...(ownerFields.vermieter_email ? [{
              name: ownerFields.vermieter_name || 'Vermieter',
              email: ownerFields.vermieter_email, role: 'Vermieter', send_email: true
            }] : [])
          ], send_email: true
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      onSuccess(data);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="bg-gray-800/80 border border-gray-700 rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <PenLine className="w-4 h-4 text-blue-400" /> Neue Signaturanfrage
        </h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!userId && (
        <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 mb-3">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          Bitte einloggen, um Objekte und Mieter zu laden.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500">
          <option value="">Template wählen…</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={selectedProperty} onChange={e => setSelectedProperty(e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500">
          <option value="">Objekt wählen…</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.property_name || p.street_address || p.name || p.id}</option>)}
        </select>
        <select value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}
          disabled={loadingTenants || !selectedProperty}
          className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50">
          <option value="">{loadingTenants ? 'Lädt…' : 'Mieter wählen…'}</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      {ownerData && !ownerData.complete && (
        <div className="flex items-center gap-2 text-xs text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-lg px-3 py-2 mb-3">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          Vermieter-Daten unvollständig: {ownerData.missing_fields?.join(', ')}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2 mb-3">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 text-sm font-medium transition-colors">
          Abbrechen
        </button>
        <button onClick={handleSubmit} disabled={loading || !selectedTemplate || !selectedProperty || !selectedTenant}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Anfrage senden
        </button>
      </div>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────
const SignaturenPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || null;

  const [submissions, setSubmissions] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAdapter, setFilterAdapter] = useState('all');
  const [filterTemplate, setFilterTemplate] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showNeueAnfrage, setShowNeueAnfrage] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [refreshingDetail, setRefreshingDetail] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (msg, type = 'info') => {
    setToastMsg({ msg, type });
    setTimeout(() => setToastMsg(null), 3000);
  };

  const getTemplateLabelFromAudit = (submissionId, entries) => {
    const entry = entries.find(e => String(e.submission_id) === String(submissionId));
    return entry?.template_label || null;
  };

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [subRes, auditRes, tplRes] = await Promise.all([
        controlFetch('/api/control/signature/submissions?limit=50'),
        controlFetch('/api/control/signature/audit'),
        controlFetch('/api/control/signature/templates'),
      ]);
      const subData = subRes.ok ? await subRes.json() : { data: [] };
      const auditData = auditRes.ok ? await auditRes.json() : { entries: [] };
      const tplData = tplRes.ok ? await tplRes.json() : { data: [] };
      const auditList = auditData.entries || [];
      const docusealSubs = (subData.data || []).map(s => ({
        ...s,
        adapter: 'docuseal',
        template_label: getTemplateLabelFromAudit(s.id, auditList) || s.template_label,
      }));
      // Native Canvas Submissions aus Audit-Log
      const canvasSubs = auditList
        .filter(e => e.event === 'native_canvas_completed')
        .map(e => ({
          id: `canvas_${e.canvas_token?.slice(0, 8)}`,
          canvas_token: e.canvas_token,
          adapter: 'native_canvas',
          status: e.final_status === 'signed' ? 'signed' : e.final_status,
          template_label: e.template_label,
          template_id: e.template_id,
          property_id: e.property_id,
          created_at: e.signed_at || e.logged_at,
          completed_at: e.final_status === 'signed' ? (e.signed_at || e.logged_at) : null,
          audit_id: e.audit_id,
          vault_asset_id: e.vault_asset_id,
          signed_by_tenant: e.signed_by_tenant,
          signed_by_owner: e.signed_by_owner,
          submitters: [
            e.signed_by_tenant && { name: e.signed_by_tenant, role: 'Mieter', status: 'completed' },
            e.signed_by_owner && { name: e.signed_by_owner, role: 'Vermieter', status: 'completed' },
          ].filter(Boolean),
        }));
      const all = [...docusealSubs, ...canvasSubs].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setSubmissions(all);
      setAuditEntries(auditList);
      setTemplates(tplData.data || []);
    } catch (err) {
      setError('Daten konnten nicht geladen werden: ' + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = () => { setRefreshing(true); loadData(true); };

  const handleCancel = async (submissionId) => {
    if (String(submissionId).startsWith('canvas_')) {
      showToast('Vor-Ort-Signaturen können nicht storniert werden.', 'error'); return;
    }
    setCancelling(true);
    try {
      const res = await controlFetch(`/api/control/signature/submissions/${submissionId}/cancel`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Submission storniert.', 'success');
      setSelectedSubmission(null);
      loadData(true);
    } catch (err) {
      showToast('Stornierung fehlgeschlagen: ' + err.message, 'error');
    } finally { setCancelling(false); }
  };

  const handleRefreshDetail = async (submissionId) => {
    if (String(submissionId).startsWith('canvas_')) return;
    setRefreshingDetail(true);
    try {
      const res = await controlFetch(`/api/control/signature/status/${submissionId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubmissions(prev => prev.map(s =>
        String(s.id) === String(submissionId)
          ? { ...s, status: data.status, submitters: data.submitters || s.submitters,
              completed_at: data.completed ? new Date().toISOString() : s.completed_at }
          : s
      ));
      if (selectedSubmission?.id === submissionId) {
        setSelectedSubmission(prev => ({
          ...prev, status: data.status, submitters: data.submitters || prev.submitters
        }));
      }
      showToast('Status aktualisiert.', 'success');
    } catch (err) {
      showToast('Aktualisierung fehlgeschlagen: ' + err.message, 'error');
    } finally { setRefreshingDetail(false); }
  };

  const filtered = submissions.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (filterAdapter !== 'all' && s.adapter !== filterAdapter) return false;
    if (filterTemplate !== 'all' && String(s.template_id) !== filterTemplate) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const searchIn = [
        s.template_label, s.property_id, String(s.id),
        ...(s.submitters || []).map(sub => `${sub.name || ''} ${sub.email || ''}`)
      ].join(' ').toLowerCase();
      if (!searchIn.includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => ['pending', 'sent', 'submitted', 'awaiting'].includes(s.status)).length,
    completed: submissions.filter(s => ['completed', 'signed'].includes(s.status)).length,
    error: submissions.filter(s => ['error', 'declined', 'expired'].includes(s.status)).length,
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Toast */}
      {toastMsg && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium ${
          toastMsg.type === 'success' ? 'bg-green-600 text-white' :
          toastMsg.type === 'error'   ? 'bg-red-600 text-white' :
          'bg-gray-700 text-white'
        }`}>
          {toastMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <Info className="w-4 h-4" />}
          {toastMsg.msg}
        </div>
      )}

      {/* Detail-Modal */}
      {selectedSubmission && (
        <DetailModal
          submission={selectedSubmission}
          auditEntries={auditEntries}
          onClose={() => setSelectedSubmission(null)}
          onCancel={handleCancel}
          onRefresh={handleRefreshDetail}
          cancelling={cancelling}
          refreshing={refreshingDetail}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <PenLine className="w-7 h-7 text-blue-400" />
              Signaturen
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">Operatives Signatur-Cockpit</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRefresh} disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm font-medium transition-colors">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Aktualisieren</span>
            </button>
            <button onClick={() => navigate('/signaturen/tablet')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm font-medium transition-colors">
              <PenLine className="w-4 h-4" />
              <span className="hidden sm:inline">Tablet-Wizard</span>
            </button>
            <button onClick={() => setShowNeueAnfrage(v => !v)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
              <Plus className="w-4 h-4" />
              Neue Anfrage
            </button>
          </div>
        </div>

        {/* Statistik-Kacheln */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Gesamt',          value: stats.total,     color: 'text-white',      bg: 'bg-gray-700/40' },
            { label: 'Offen',           value: stats.pending,   color: 'text-yellow-400', bg: 'bg-yellow-400/5 border border-yellow-400/20' },
            { label: 'Abgeschlossen',   value: stats.completed, color: 'text-green-400',  bg: 'bg-green-400/5 border border-green-400/20' },
            { label: 'Fehler/Abgelehnt',value: stats.error,     color: 'text-red-400',    bg: 'bg-red-400/5 border border-red-400/20' },
          ].map((stat, i) => (
            <div key={i} className={`rounded-xl p-4 ${stat.bg}`}>
              <p className="text-xs text-gray-400 mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Neue Anfrage */}
        {showNeueAnfrage && (
          <NeueAnfrage
            userId={userId}
            onClose={() => setShowNeueAnfrage(false)}
            onSuccess={() => { setShowNeueAnfrage(false); showToast('Signaturanfrage erstellt!', 'success'); loadData(true); }}
          />
        )}

        {/* Filter & Suche */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Suche nach Mieter, Objekt, ID…"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-xl pl-9 pr-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
            <option value="all">Alle Status</option>
            <option value="pending">Ausstehend</option>
            <option value="sent">Versendet</option>
            <option value="completed">Abgeschlossen</option>
            <option value="signed">Signiert</option>
            <option value="declined">Abgelehnt</option>
            <option value="expired">Abgelaufen</option>
            <option value="cancelled">Storniert</option>
            <option value="error">Fehler</option>
          </select>
          <select value={filterAdapter} onChange={e => setFilterAdapter(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
            <option value="all">Alle Modi</option>
            <option value="docuseal">DocuSeal (E-Mail)</option>
            <option value="native_canvas">Vor-Ort (Tablet)</option>
          </select>
          <select value={filterTemplate} onChange={e => setFilterTemplate(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500">
            <option value="all">Alle Templates</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 bg-red-400/10 border border-red-400/20 rounded-xl p-5 text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Fehler beim Laden</p>
              <p className="text-sm mt-0.5">{error}</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Keine Submissions gefunden</p>
            <p className="text-sm mt-1">Filter anpassen oder neue Anfrage starten</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(s => {
              const adapterCfg = getAdapter(s.adapter);
              return (
                <button key={s.id} onClick={() => setSelectedSubmission(s)}
                  className="w-full text-left bg-gray-800/60 hover:bg-gray-800 border border-gray-700/50 hover:border-gray-600 rounded-xl p-4 transition-all group">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${adapterCfg.bg}`}>
                      {s.adapter === 'native_canvas'
                        ? <PenLine className={`w-5 h-5 ${adapterCfg.color}`} />
                        : <Send className={`w-5 h-5 ${adapterCfg.color}`} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white truncate">
                          {s.template_label || `Submission #${s.id}`}
                        </span>
                        <StatusBadge status={s.status} />
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${adapterCfg.bg} ${adapterCfg.color}`}>
                          {adapterCfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {fmtDate(s.created_at)}
                        </span>
                        {s.submitters?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {s.submitters.map(sub => sub.name).filter(Boolean).join(', ')}
                          </span>
                        )}
                        {s.id && !String(s.id).startsWith('canvas_') && (
                          <span className="font-mono">#{s.id}</span>
                        )}
                      </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-gray-300 transition-colors flex-shrink-0 -rotate-90" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && (
          <p className="text-xs text-gray-500 text-center mt-4">
            {filtered.length} von {submissions.length} Submissions
          </p>
        )}
      </div>
    </div>
  );
};

export default SignaturenPage;
